import { Link } from 'react-router-dom'
import { Upload, Sparkles, FileText, ArrowRight } from 'lucide-react'
import { Logo } from '../components/Logo'
import { AnimatedExplainer } from '../components/AnimatedExplainer'

export function HowItWorks() {
  return (
    <div className="min-h-screen landing-gradient">
      {/* Header */}
      <header style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 50,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Logo />
            </Link>

            <Link
              to="/generate"
              className="btn-gold-pulse font-bold transition-all duration-200 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #D4AF37 100%)',
                color: '#0A0A0F',
                padding: '0.6rem 1.5rem',
                borderRadius: '999px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textDecoration: 'none',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            >
              Try It Free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-5xl mx-auto px-6 py-16 pt-28 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 text-foreground" style={{ fontFamily: 'Playfair Display, serif' }}>
          How The Caption Studio Works
        </h1>
        <p className="text-xl text-muted max-w-2xl mx-auto">
          Transform your photos into captivating captions in three simple steps
        </p>
      </section>

      {/* Video Section - Animated Explainer */}
      <section className="max-w-4xl mx-auto px-6 mb-20">
        <div className="relative rounded-2xl shadow-2xl overflow-hidden border-2 border-[#D4AF37]/30 aspect-video">
          <AnimatedExplainer />
        </div>
      </section>

      {/* Steps Section */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="space-y-16">
          
          {/* Step 1 */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1 order-2 md:order-1 p-8 rounded-2xl" style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div className="inline-flex items-center justify-center text-white text-4xl font-bold rounded-full w-16 h-16 mb-4" style={{
                background: 'rgba(209,209,247,0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 0 20px rgba(209,209,247,0.35), 0 0 40px rgba(142,143,250,0.15)'
              }}>
                1
              </div>
              <h2 className="text-3xl font-bold mb-4 text-foreground" style={{ fontFamily: 'Playfair Display, serif' }}>
                Upload Your Image
              </h2>
              <p className="text-lg text-muted leading-relaxed mb-4">
                Drop your photo and watch the magic happen. Our AI instantly detects objects, people, and vibes—no manual tagging needed.
              </p>
              <ul className="space-y-2 text-muted">
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>Supports JPEG, PNG, and WebP formats</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>AI analyzes in seconds</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>Detects objects, people, and context</span>
                </li>
              </ul>
            </div>
            <div className="flex-1 order-1 md:order-2">
              <div className="bg-gradient-to-br from-[#235C45]/10 to-[#D4AF37]/10 rounded-2xl p-12 flex items-center justify-center border border-[#D4AF37]/20">
                <Upload className="w-32 h-32" strokeWidth={1.5} style={{ color: '#8E8FFA', filter: 'drop-shadow(0 0 16px rgba(142,143,250,0.7)) drop-shadow(0 0 32px rgba(142,143,250,0.3))' }} />
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1">
              <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#235C45]/10 rounded-2xl p-12 flex items-center justify-center border border-[#D4AF37]/20">
                <Sparkles className="w-32 h-32" strokeWidth={1.5} style={{ color: '#D4AF37', filter: 'drop-shadow(0 0 16px rgba(212,175,55,0.7)) drop-shadow(0 0 32px rgba(212,175,55,0.3))' }} />
              </div>
            </div>
            <div className="flex-1 p-8 rounded-2xl" style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div className="inline-flex items-center justify-center text-white text-4xl font-bold rounded-full w-16 h-16 mb-4" style={{
                background: 'rgba(212,175,55,0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 0 20px rgba(212,175,55,0.35), 0 0 40px rgba(212,175,55,0.15)'
              }}>
                2
              </div>
              <h2 className="text-3xl font-bold mb-4 text-foreground" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lock In Your Vibe
              </h2>
              <p className="text-lg text-muted leading-relaxed mb-4">
                Pick how you want to sound: Lowkey, No Cap, Poetic, or go Unhinged for something chaotic. Your vibe shapes the lore of your post.
              </p>
              <ul className="space-y-2 text-muted">
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>8+ Unique, Gen Z-coded mood profiles</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>Smart-filtering: We skip the boring location descriptions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>Pinterest-style 'Poetic' fragments for high-aesthetic posts</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1 order-2 md:order-1 p-8 rounded-2xl" style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div className="inline-flex items-center justify-center text-white text-4xl font-bold rounded-full w-16 h-16 mb-4" style={{
                background: 'rgba(224,64,251,0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 0 20px rgba(224,64,251,0.35), 0 0 40px rgba(212,0,255,0.15)'
              }}>
                3
              </div>
              <h2 className="text-3xl font-bold mb-4 text-foreground" style={{ fontFamily: 'Playfair Display, serif' }}>
                Cooked to Perfection
              </h2>
              <p className="text-lg text-muted leading-relaxed mb-4">
                Receive multiple unique captions instantly, complete with relevant emojis and hashtags. Copy your favorite and watch the engagement roll in.
              </p>
              <ul className="space-y-2 text-muted">
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>Human-coded rhythm (Short & Long variations)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>No more 'robot-speak' or generic English</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#D4AF37] mr-2">✓</span>
                  <span>One-tap copy to clipboard for instant posting</span>
                </li>
              </ul>
            </div>
            <div className="flex-1 order-1 md:order-2">
              <div className="bg-gradient-to-br from-[#235C45]/10 to-[#D4AF37]/10 rounded-2xl p-12 flex items-center justify-center border border-[#D4AF37]/20">
                <FileText className="w-32 h-32" strokeWidth={1.5} style={{ color: '#e040fb', filter: 'drop-shadow(0 0 16px rgba(224,64,251,0.7)) drop-shadow(0 0 32px rgba(224,64,251,0.3))' }} />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="rounded-2xl p-12 text-center text-white shadow-xl" style={{
          background: 'linear-gradient(145deg, rgba(15,15,15,0.8), rgba(26,26,46,0.9))',
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#ffffff', textShadow: '0 2px 12px rgba(0,0,0,0.4), 0 0 30px rgba(255,255,255,0.1)' }}>
            Ready to Transform Your Captions?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start creating captivating captions in seconds
          </p>
          <Link
            to="/generate"
            className="btn-gold-pulse inline-flex items-center gap-2 font-bold transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #D4AF37 100%)',
              color: '#0A0A0F',
              padding: '1rem 2rem',
              borderRadius: '999px',
              fontSize: '1.1rem',
              fontWeight: 700,
              textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.3)'
            }}
          >
            Try It Free Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card/80 backdrop-blur-sm border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-3">
              <Logo showText={false} />
              <span className="text-sm text-muted">© 2026 The Caption Studio. Made in India 🇮🇳</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/how-it-works" className="transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}>
                How It Works
              </Link>
              <Link to="/privacy" className="transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}>
                Privacy Policy
              </Link>
              <Link to="/terms" className="transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}>
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
